import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { checklistsApi, typesApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertCircle,
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  GripVertical,
  Eye,
  Upload,
  Download,
  Copy,
  ChevronDown,
  ChevronUp,
  Loader2,
  CheckCircle,
  X,
  AlertTriangle,
  ImageIcon
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

// Item Editor Component
function ChecklistItemEditor({ item, index, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast, categories }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [criteriaInput, setCriteriaInput] = useState('');

  const handleChange = (field, value) => {
    onUpdate(index, { ...item, [field]: value });
  };

  const addCriteria = () => {
    if (criteriaInput.trim()) {
      const newCriteria = [...(item.criteria || []), criteriaInput.trim()];
      handleChange('criteria', newCriteria);
      setCriteriaInput('');
    }
  };

  const removeCriteria = (criteriaIndex) => {
    const newCriteria = item.criteria.filter((_, i) => i !== criteriaIndex);
    handleChange('criteria', newCriteria);
  };

  return (
    <div className="border border-border/50 rounded-lg p-4 bg-card/50">
      <div className="flex items-start gap-3">
        {/* Drag handle and order buttons */}
        <div className="flex flex-col items-center gap-1 pt-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-6 w-6"
            onClick={() => onMoveUp(index)}
            disabled={isFirst}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <GripVertical className="h-4 w-4 text-muted-foreground" />
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-6 w-6"
            onClick={() => onMoveDown(index)}
            disabled={isLast}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </div>

        {/* Main content */}
        <div className="flex-1 space-y-3">
          {/* Header row */}
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-xs shrink-0">
              #{index + 1}
            </Badge>
            <Input
              value={item.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Prüfpunkt-Name"
              className="flex-1 font-medium"
            />
            <Select value={item.severity} onValueChange={(v) => handleChange('severity', v)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="critical">Kritisch</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          {/* Category selection */}
          <div className="flex gap-3">
            <Select value={item.category} onValueChange={(v) => handleChange('category', v)}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Kategorie wählen" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
                <SelectItem value="custom">+ Neue Kategorie</SelectItem>
              </SelectContent>
            </Select>
            {item.category === 'custom' && (
              <Input
                placeholder="Neue Kategorie eingeben"
                onChange={(e) => handleChange('category', e.target.value)}
                className="flex-1"
              />
            )}
          </div>

          {/* Expanded details */}
          {isExpanded && (
            <div className="space-y-4 pt-3 border-t border-border/50">
              {/* Description */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Kurzbeschreibung</Label>
                <Input
                  value={item.description || ''}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Kurze Beschreibung des Prüfpunkts"
                />
              </div>

              {/* Help text */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Hilfetext (für Laien)</Label>
                <Textarea
                  value={item.help_text || ''}
                  onChange={(e) => handleChange('help_text', e.target.value)}
                  placeholder="Ausführliche Erklärung, was geprüft werden soll..."
                  rows={3}
                />
              </div>

              {/* Help image */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground flex items-center gap-2">
                  <ImageIcon className="h-3 w-3" />
                  Beispielbild URL
                </Label>
                <Input
                  value={item.help_image || ''}
                  onChange={(e) => handleChange('help_image', e.target.value)}
                  placeholder="https://images.unsplash.com/... oder https://images.pexels.com/..."
                />
                {item.help_image && (
                  <div className="mt-2 relative">
                    <img 
                      src={item.help_image} 
                      alt="Beispielbild Vorschau"
                      className="max-h-32 rounded-lg border border-border object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      onLoad={(e) => {
                        e.target.style.display = 'block';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'none';
                      }}
                    />
                    <div 
                      className="hidden items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive"
                    >
                      <AlertCircle className="h-4 w-4" />
                      Bild konnte nicht geladen werden. Bitte URL prüfen.
                    </div>
                  </div>
                )}
              </div>

              {/* Legal reference */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Gesetzesreferenz</Label>
                <Input
                  value={item.legal_reference || ''}
                  onChange={(e) => handleChange('legal_reference', e.target.value)}
                  placeholder="z.B. VKF 15-15 Ziffer 3.3"
                />
              </div>

              {/* Criteria */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Prüfkriterien</Label>
                <div className="flex gap-2">
                  <Input
                    value={criteriaInput}
                    onChange={(e) => setCriteriaInput(e.target.value)}
                    placeholder="Neues Kriterium hinzufügen"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCriteria())}
                  />
                  <Button type="button" variant="outline" onClick={addCriteria}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {item.criteria && item.criteria.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {item.criteria.map((criterion, i) => (
                      <Badge key={i} variant="secondary" className="pl-2 pr-1 py-1 gap-1">
                        {criterion}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-4 w-4 p-0 hover:bg-transparent"
                          onClick={() => removeCriteria(i)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Preview Component
function ChecklistPreview({ checklist }) {
  const groupedItems = checklist.items.reduce((acc, item) => {
    const category = item.category || 'Allgemein';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  return (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto">
      <div className="p-4 bg-muted/50 rounded-lg">
        <h3 className="font-semibold text-lg">{checklist.name}</h3>
        <p className="text-sm text-muted-foreground">{checklist.description}</p>
        <div className="flex gap-2 mt-2">
          <Badge>{checklist.building_type}</Badge>
          <Badge variant="outline">{checklist.items.length} Prüfpunkte</Badge>
        </div>
      </div>

      <Accordion type="multiple" className="space-y-2">
        {Object.entries(groupedItems).map(([category, items]) => (
          <AccordionItem key={category} value={category} className="border rounded-lg">
            <AccordionTrigger className="px-4 py-2 hover:no-underline">
              <div className="flex items-center gap-2">
                <span className="font-medium">{category}</span>
                <Badge variant="secondary" className="text-xs">{items.length}</Badge>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-3">
                {items.map((item, idx) => (
                  <div key={item.id || idx} className="p-3 bg-card border rounded-lg">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{item.name}</span>
                          {item.severity === 'critical' && (
                            <Badge variant="destructive" className="text-xs">Kritisch</Badge>
                          )}
                        </div>
                        {item.description && (
                          <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="text-green-600 border-green-600">OK</Button>
                        <Button size="sm" variant="outline" className="text-red-600 border-red-600">Mangel</Button>
                        <Button size="sm" variant="outline">N/A</Button>
                      </div>
                    </div>
                    
                    {(item.help_text || item.legal_reference || item.criteria?.length > 0) && (
                      <div className="mt-3 p-3 bg-muted/50 rounded-lg text-sm">
                        {item.help_text && (
                          <div className="mb-2">
                            <span className="font-medium">Erklärung: </span>
                            <span className="text-muted-foreground">{item.help_text}</span>
                          </div>
                        )}
                        {item.legal_reference && (
                          <div className="mb-2">
                            <span className="font-medium">Gesetzliche Grundlage: </span>
                            <span className="text-muted-foreground">{item.legal_reference}</span>
                          </div>
                        )}
                        {item.criteria?.length > 0 && (
                          <div>
                            <span className="font-medium">Prüfkriterien: </span>
                            <ul className="list-disc list-inside text-muted-foreground">
                              {item.criteria.map((c, i) => <li key={i}>{c}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}

// Main Editor Component
export default function ChecklistEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importJson, setImportJson] = useState('');

  const [checklist, setChecklist] = useState({
    name: '',
    building_type: '',
    inspection_type: 'yearly',
    description: '',
    items: []
  });

  // Standard categories
  const standardCategories = [
    'A1. Baustoffe und Bauteile',
    'A2. Brandschutzabstände',
    'A3. Tragwerke und Brandabschnitte',
    'A4. Flucht- und Rettungswege',
    'B1. Brandmeldeanlage',
    'B2. Feuerlöscher',
    'B3. Sprinkleranlage',
    'B4. Rauch- und Wärmeabzug',
    'B5. Blitzschutz',
    'B6. Sicherheitsbeleuchtung',
    'C1. Heizungen',
    'C2. Lüftungen',
    'C3. Elektroinstallationen',
    'C4. Aufzüge',
    'C5. Solaranlagen',
    'D1. Organisation',
    'D2. Dokumentation',
    'D3. Besondere Risiken',
    'D4. Aussenbereich',
    'VS. Walliser Spezialvorschriften'
  ];

  // Get unique categories from items
  const existingCategories = [...new Set(checklist.items.map(item => item.category).filter(Boolean))];
  const allCategories = [...new Set([...standardCategories, ...existingCategories])].sort();

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const typesRes = await typesApi.getBuildingTypes();
      setBuildingTypes(typesRes.data || []);

      if (!isNew && id && id !== 'new') {
        const checklistRes = await checklistsApi.getById(id);
        setChecklist(checklistRes.data);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      // Don't show error for new checklists if building types fail
      if (!isNew) {
        setError('Fehler beim Laden der Daten');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!checklist.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }
    if (!checklist.building_type) {
      setError('Gebäudetyp ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Ensure items have proper IDs
      const itemsWithIds = checklist.items.map((item, idx) => ({
        ...item,
        id: item.id || `item-${idx}`,
        order: idx
      }));

      const dataToSave = {
        ...checklist,
        items: itemsWithIds
      };

      if (isNew) {
        const response = await checklistsApi.create(dataToSave);
        setSuccess('Checkliste erstellt!');
        setTimeout(() => navigate(`/admin/checklists/${response.data.id}`), 1500);
      } else {
        await checklistsApi.update(id, dataToSave);
        setSuccess('Checkliste gespeichert!');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await checklistsApi.export(id);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${checklist.name.replace(/\s+/g, '_')}_export.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Fehler beim Exportieren');
    }
  };

  const handleImport = async () => {
    try {
      const data = JSON.parse(importJson);
      const response = await checklistsApi.import(data);
      setSuccess('Checkliste importiert!');
      setShowImportDialog(false);
      setImportJson('');
      setTimeout(() => navigate(`/admin/checklists/${response.data.id}`), 1500);
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Ungültiges JSON-Format');
      } else {
        setError(err.response?.data?.detail || 'Fehler beim Importieren');
      }
    }
  };

  const addItem = () => {
    const newItem = {
      id: `new-${Date.now()}`,
      name: '',
      description: '',
      category: standardCategories[0],
      order: checklist.items.length,
      help_text: '',
      help_image: '',
      legal_reference: '',
      criteria: [],
      severity: 'normal'
    };
    setChecklist(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const updateItem = (index, updatedItem) => {
    setChecklist(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? updatedItem : item)
    }));
  };

  const deleteItem = (index) => {
    setChecklist(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const moveItem = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= checklist.items.length) return;
    
    const items = [...checklist.items];
    [items[index], items[newIndex]] = [items[newIndex], items[index]];
    setChecklist(prev => ({ ...prev, items }));
  };

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Card className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-lg font-semibold">Zugriff verweigert</h2>
            <p className="text-muted-foreground">Nur Administratoren können Checklisten bearbeiten.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom duration-500">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/checklists')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl">
                {isNew ? 'Neue Checkliste' : 'Checkliste bearbeiten'}
              </h1>
              <p className="text-muted-foreground text-sm">
                {checklist.items.length} Prüfpunkte
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {!isNew && (
              <>
                <Button variant="outline" onClick={handleExport}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </>
            )}
            <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Checkliste importieren</DialogTitle>
                  <DialogDescription>
                    Fügen Sie das JSON einer exportierten Checkliste ein.
                  </DialogDescription>
                </DialogHeader>
                <Textarea
                  value={importJson}
                  onChange={(e) => setImportJson(e.target.value)}
                  placeholder='{"name": "...", "building_type": "...", "items": [...]}'
                  rows={10}
                  className="font-mono text-sm"
                />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowImportDialog(false)}>
                    Abbrechen
                  </Button>
                  <Button onClick={handleImport}>
                    Importieren
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={showPreview} onOpenChange={setShowPreview}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Eye className="h-4 w-4 mr-2" />
                  Vorschau
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh]">
                <DialogHeader>
                  <DialogTitle>Vorschau</DialogTitle>
                </DialogHeader>
                <ChecklistPreview checklist={checklist} />
              </DialogContent>
            </Dialog>

            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Speichern
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <span className="text-destructive">{error}</span>
            <Button variant="ghost" size="icon" className="ml-auto" onClick={() => setError(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        {success && (
          <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span className="text-green-600">{success}</span>
          </div>
        )}

        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Grunddaten</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name der Checkliste *</Label>
                <Input
                  id="name"
                  value={checklist.name}
                  onChange={(e) => setChecklist(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="z.B. Hotel/Beherbergung - Vollständige Kontrolle"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="building_type">Gebäudetyp *</Label>
                <Select 
                  value={checklist.building_type} 
                  onValueChange={(v) => setChecklist(prev => ({ ...prev, building_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Gebäudetyp wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {buildingTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="inspection_type">Kontrolltyp</Label>
                <Select 
                  value={checklist.inspection_type} 
                  onValueChange={(v) => setChecklist(prev => ({ ...prev, inspection_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yearly">Jährliche Kontrolle</SelectItem>
                    <SelectItem value="initial">Neubau/Umbau-Abnahme</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={checklist.description}
                onChange={(e) => setChecklist(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Beschreibung der Checkliste..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Items */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Prüfpunkte ({checklist.items.length})</CardTitle>
            <Button onClick={addItem}>
              <Plus className="h-4 w-4 mr-2" />
              Prüfpunkt hinzufügen
            </Button>
          </CardHeader>
          <CardContent>
            {checklist.items.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Noch keine Prüfpunkte vorhanden.</p>
                <Button variant="outline" className="mt-4" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-2" />
                  Ersten Prüfpunkt hinzufügen
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {checklist.items.map((item, index) => (
                  <ChecklistItemEditor
                    key={item.id || index}
                    item={item}
                    index={index}
                    onUpdate={updateItem}
                    onDelete={deleteItem}
                    onMoveUp={() => moveItem(index, -1)}
                    onMoveDown={() => moveItem(index, 1)}
                    isFirst={index === 0}
                    isLast={index === checklist.items.length - 1}
                    categories={allCategories}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bottom save button for long lists */}
        {checklist.items.length > 5 && (
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} size="lg">
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Checkliste speichern
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
